/**
 * AdminFirmCases — firm → cases drill-down (admin dashboard §1/§3).
 * Lists every case for a firm with posture (type) badges, status chips, event
 * date, claim amount, and the per-case seat lens (reuses ViewAsMenu). Consumes
 * the existing GET /api/v1/law-firms/:id/clients (extended with `posture`).
 *
 * Coded by kimi 2.6 to a conductor-verified spec; conductor-reviewed (the real
 * backend resolver is `resolve as resolve_case_profile`, not a `resolve_case_profile`
 * export — fixed server-side) + date formatting added here.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronRight, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/TestAuthContext';
import { api } from '../../utils/api';
import AppLayout from '../../components/AppLayout';
import ViewAsMenu from '../../components/admin/ViewAsMenu';
import AdminFirmRoster from '../../components/admin/AdminFirmRoster';
import { CASE_FACETS, postureBadgeLabel, damagesBadgeLabel, type CaseFacetCase } from '../../config/caseTypes';

interface LawFirm {
  id: string;
  name: string;
  client_side?: string | null;
}

interface CaseItem {
  id: string;
  claimant_name?: string;
  business_name?: string;
  event_type?: string;
  event_date?: string;
  status?: string;
  created_at?: string;
  claim_amount?: number | null;
  posture?: 'first_party' | 'third_party' | null;
  damages_model?: 'standard_bi' | 'future_closure' | null;
}

interface ClientsResponse {
  clients: CaseItem[];
  total: number;
}

const fmtMoney = (n?: number | null) => {
  if (n === null || n === undefined || n === 0) return '—';
  return `$${n.toLocaleString()}`;
};

// ISO + tabular (K3 design gate 2026-10-09): evidentiary dates read as YYYY-MM-DD, monospace.
const fmtDate = (d?: string) => {
  if (!d) return '';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return d;
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

// Court-exhibit standard: never surface a raw underscored DB enum (K3 gate 2026-10-08) —
// "IN_REVIEW" -> "IN REVIEW", consistent with the FIRST-PARTY/THIRD-PARTY badges.
const fmtStatus = (s?: string) => (s ? s.replace(/_/g, ' ') : s);

export default function AdminFirmCases() {
  const { id } = useParams<{ id: string }>();
  const { getToken } = useAuth();
  const [firm, setFirm] = useState<LawFirm | null>(null);
  const [cases, setCases] = useState<CaseItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeFacets, setActiveFacets] = useState<Record<string, string>>(
    () => Object.fromEntries(CASE_FACETS.map((g) => [g.id, 'all']))
  );

  useEffect(() => {
    let active = true;
    if (!id) {
      setLoading(false);
      setError('Missing firm ID.');
      return;
    }
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await getToken();
        const [firmData, clientsData] = await Promise.all([
          api.get<LawFirm>(`/api/v1/law-firms/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
          api.get<ClientsResponse>(`/api/v1/law-firms/${id}/clients`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        if (active) {
          setFirm(firmData);
          setCases(clientsData?.clients || []);
          setTotal(clientsData?.total ?? 0);
        }
      } catch (e: any) {
        if (active) setError(e?.message || 'Could not load firm cases.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [id, getToken]);

  const filteredCases = useMemo(() => {
    return cases.filter((c) =>
      CASE_FACETS.every((g) => {
        const opt = g.options.find((o) => o.id === activeFacets[g.id]);
        return opt ? opt.predicate(c as CaseFacetCase) : true;
      })
    );
  }, [cases, activeFacets]);

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto space-y-8 p-6">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--color-text-muted)' }} />
          </div>
        ) : error ? (
          <p className="py-10 text-center text-sm" style={{ color: 'var(--color-needs-human-text)' }}>{error}</p>
        ) : (
          <>
            <div>
              {/* In-page breadcrumb (AppLayout has no breadcrumb bar): "Firms" links back to the
                  firms list — the only in-app path back — and the current firm name is the trailing
                  active segment. Replaces the removed back-link; shell carries no breadcrumb. */}
              <nav className="flex items-center gap-1 mb-1 cs-meta font-mono uppercase tracking-wide" aria-label="Breadcrumb">
                <Link
                  to="/admin/firms"
                  className="transition-colors hover:underline hover:text-[var(--color-text-secondary)]"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  Firms
                </Link>
                <ChevronRight className="w-3.5 h-3.5" style={{ color: 'var(--color-text-dim)' }} />
                <span aria-current="page" style={{ color: 'var(--color-text-secondary)' }}>
                  {firm?.name || 'Firm'}
                </span>
              </nav>
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="cs-h1" style={{ color: 'var(--color-text-primary)' }}>{firm?.name || 'Firm'}</h1>
                {firm?.client_side === 'carrier' && (
                  <span className="font-mono text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded"
                    style={{ color: 'var(--color-needs-human-text)', border: '1px solid var(--color-needs-human-text)' }}>
                    carrier
                  </span>
                )}
              </div>
              <p className="cs-meta mt-1 font-mono uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>
                {CASE_FACETS.some((g) => activeFacets[g.id] !== 'all')
                  ? `${filteredCases.length} OF ${total} CASE${total === 1 ? '' : 'S'}`
                  : `${total} CASE${total === 1 ? '' : 'S'}`}
              </p>
            </div>

            <div className="space-y-3">
              {CASE_FACETS.map((group) => (
                <div key={group.id} className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-[10px] uppercase tracking-wide shrink-0 w-28" style={{ color: 'var(--color-text-dim)' }}>
                    {group.label}
                  </span>
                  {group.options.map((opt) => {
                    const selected = activeFacets[group.id] === opt.id;
                    return (
                      <button key={opt.id} type="button" onClick={() => setActiveFacets((prev) => ({ ...prev, [group.id]: opt.id }))}
                        className="font-mono text-[10px] uppercase tracking-wide px-3 py-1.5 rounded transition-colors"
                        style={{
                          border: selected ? '1px solid var(--color-text-muted)' : '1px solid var(--color-border)',
                          color: selected ? 'var(--color-text-primary)' : 'var(--color-text-muted)',
                          background: 'transparent',
                        }}>
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>

            <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg-surface)' }}>
              {filteredCases.length === 0 ? (
                <p className="py-12 text-center text-sm" style={{ color: 'var(--color-text-muted)' }}>
                  {cases.length === 0 ? 'No cases for this firm yet.' : 'No cases match this filter.'}
                </p>
              ) : (
                filteredCases.map((c, i) => {
                  const name = c.business_name || c.claimant_name || 'Unnamed case';
                  return (
                    <div key={c.id} className="flex items-center gap-4 px-5 py-4"
                      style={{ borderTop: i ? '1px solid var(--color-border)' : 'none' }}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>{name}</span>
                          <span className="font-mono text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded"
                            style={{
                              color: 'var(--color-text-muted)',
                              border: '1px solid var(--color-text-muted)',
                            }}>
                            {postureBadgeLabel(c.posture)}
                          </span>
                          {c.damages_model === 'future_closure' && (
                            <span className="font-mono text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded"
                              style={{
                                color: 'var(--color-text-muted)',
                                border: '1px solid var(--color-text-muted)',
                              }}>
                              {damagesBadgeLabel(c.damages_model)}
                            </span>
                          )}
                          {c.status && (
                            <span className="font-mono text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded"
                              style={{ color: 'var(--color-text-muted)', border: '1px solid var(--color-border)' }}>
                              {fmtStatus(c.status)}
                            </span>
                          )}
                        </div>
                        <div className="mt-1 text-xs font-mono tabular-nums" style={{ color: 'var(--color-text-dim)' }}>
                          {fmtDate(c.event_date || c.created_at)}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-mono" style={{ color: 'var(--color-text-primary)' }}>{fmtMoney(c.claim_amount)}</div>
                      </div>
                      <div className="flex-shrink-0">
                        <ViewAsMenu caseId={c.id} />
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className="pt-8" style={{ borderTop: '1px solid var(--color-border)' }}>
              <AdminFirmRoster firmId={id!} />
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
}
